import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import type { ArtifactsApi } from "../../../api/artifactsApi";

export interface ArtifactApprovalBarProps {
  artifactId: string;
  api: ArtifactsApi;
}

type Outcome = "idle" | "approved" | "rejected";

/** Approve / Reject controls for a review-gated artifact. The gate is
 *  enforced server-side; an approve/reject on a non-pending artifact
 *  returns an error which we show inline. (Edit is deferred this round.) */
export function ArtifactApprovalBar({ artifactId, api }: ArtifactApprovalBarProps) {
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>("idle");
  const [error, setError] = useState<string | null>(null);

  const act = (fn: () => Promise<unknown>, done: Outcome) => async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      setOutcome(done);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (outcome !== "idle") {
    return (
      <Box sx={{ px: 1.25, py: 0.75, borderTop: 1, borderColor: "divider" }}>
        <Typography variant="caption" sx={{ color: outcome === "approved" ? "success.main" : "text.secondary" }}>
          {outcome === "approved" ? "Approved" : "Rejected"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1.25, py: 0.75, borderTop: 1, borderColor: "divider" }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Button size="small" variant="contained" color="success" disabled={busy}
          onClick={act(() => api.approve(artifactId), "approved")}>
          Approve
        </Button>
        <Button size="small" variant="outlined" disabled={busy}
          onClick={act(() => api.reject(artifactId), "rejected")}>
          Reject
        </Button>
      </Box>
      {error && (
        <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
